// The figures behind a deal's details panel: where it ranks today, how it compares
// with buying points outright, what each Points Club level would pay, when the
// offer ends, and the alert threshold that would catch deals like it. Every value
// is derived from the feed item and the visitor's settings; nothing is estimated.
import {
  formatAud,
  marketplaceMetrics,
  melbourneCalendarDate,
  toAmount,
  toDate,
  wineMetrics,
} from './dealFormatting';
import { BUY_POINTS_CENTS_PER_POINT } from './pointsValue';

const round2 = (value) => Math.round(value * 100) / 100;

// Competition ranking by cents per point at two decimals, as the table shows it:
// equal values share a place (1, 1, 3). Returns key -> place.
export const rankByValue = (deals, keyOf, centsOf) => {
  const values = deals
    .map((deal) => ({ key: keyOf(deal), cents: centsOf(deal) }))
    .filter(({ key, cents }) => key && Number.isFinite(cents) && cents > 0)
    .map(({ key, cents }) => ({ key, cents: round2(cents) }));
  const sorted = values.map(({ cents }) => cents).sort((a, b) => a - b);
  return new Map(values.map(({ key, cents }) => [key, 1 + sorted.findIndex((value) => value === cents)]));
};

// Against Qantas' points purchase price (about 2.67¢).
export const valueVerdict = (centsPerPoint) => {
  if (!Number.isFinite(centsPerPoint) || centsPerPoint <= 0) return null;
  const difference = round2(Math.abs(BUY_POINTS_CENTS_PER_POINT - centsPerPoint));
  if (difference < 0.01) return { tone: 'even', text: 'About the same as buying points' };
  return centsPerPoint < BUY_POINTS_CENTS_PER_POINT
    ? { tone: 'good', text: `${difference.toFixed(2)}¢ cheaper than buying points` }
    : { tone: 'poor', text: `${difference.toFixed(2)}¢ more than buying points` };
};

// Positions (0–100) on a scale that fits both this deal and the buying price.
export const valueScale = (centsPerPoint) => {
  const low = Math.min(centsPerPoint, BUY_POINTS_CENTS_PER_POINT) * 0.6;
  const high = Math.max(centsPerPoint, BUY_POINTS_CENTS_PER_POINT) * 1.2;
  const at = (value) => Math.round(((value - low) / (high - low)) * 1000) / 10;
  return { deal: at(centsPerPoint), buy: at(BUY_POINTS_CENTS_PER_POINT) };
};

// What the same points would cost bought outright from Qantas.
export const buyingCost = (points) => (points * BUY_POINTS_CENTS_PER_POINT) / 100;

const DAY_MONTH = new Intl.DateTimeFormat('en-AU', { day: 'numeric', month: 'short', timeZone: 'Australia/Melbourne' });
const DAY_MS = 86400000;

// "Ends today", "Ends tomorrow", "Ends 31 Oct, in 18 days", in Melbourne days.
export const endsLabel = (validUntil, now = Date.now()) => {
  const end = toDate(validUntil);
  if (!end) return 'End date not stated';
  const days = Math.round((Date.parse(melbourneCalendarDate(end)) - Date.parse(melbourneCalendarDate(now))) / DAY_MS);
  if (days < 0) return `Ended ${DAY_MONTH.format(end)}`;
  if (days === 0) return 'Ends today';
  if (days === 1) return `Ends tomorrow, ${DAY_MONTH.format(end)}`;
  return `Ends ${DAY_MONTH.format(end)}, in ${days} days`;
};

export const WINE_TIERS = [
  { value: 'standard', label: 'None' },
  { value: 'pointsClub', label: 'Points Club' },
  { value: 'pointsClubPlus', label: 'Points Club Plus' },
];

// Cents per point at each Points Club level, without and with the $50 voucher.
export const wineMembershipComparison = (rawWine, { membershipTier, voucherAmount }) => ({
  title: 'With your membership',
  columns: ['¢ per point', '+ $50 voucher'],
  currentColumn: voucherAmount === 50 ? 1 : 0,
  rows: WINE_TIERS.map((tier) => ({
    label: tier.label,
    current: tier.value === membershipTier,
    cells: [0, 50].map((voucher) => round2(wineMetrics(rawWine, tier.value, voucher).centsPerPoint).toFixed(2)),
  })),
});

export const WINE_ALERT_RANGE = { min: 1.5, max: 2.5 };
// Marketplace offers sit well above wine's cents per point, so its alerts allow more range.
export const MARKETPLACE_ALERT_RANGE = { min: 1.5, max: 20 };

// Alerts compare their own figures, not the tracker's: wine alerts use the price
// over the bonus points alone, Marketplace alerts the price over bonus + base. The
// threshold is that figure rounded up to the 0.1¢ step the settings offer, so a
// deal as good as this one would match, within the allowed range.
const thresholdFor = (cents, { min, max }) => {
  if (!Number.isFinite(cents) || cents <= 0) return null;
  const stepped = Math.ceil(round2(cents) * 10 - 1e-9) / 10;
  return Math.min(max, Math.max(min, stepped)).toFixed(1);
};

// Cents per point as each source's alerts compare it (see thresholdFor above).
export const wineAlertCents = (rawWine) => {
  const price = toAmount(rawWine?.price);
  const bonus = toAmount(rawWine?.points);
  return price > 0 && bonus > 0 ? (price / bonus) * 100 : null;
};

export const marketplaceAlertCents = (rawProduct) => {
  const { price, totalPoints } = marketplaceMetrics(rawProduct, true);
  return price > 0 && totalPoints > 0 ? (price / totalPoints) * 100 : null;
};

// How a threshold compares with today's deals: how many are at or under it, of how
// many, and the best. Null without deals to compare.
export const todayMatches = (cents, threshold) => {
  const values = (cents || []).filter((value) => Number.isFinite(value) && value > 0);
  const limit = Number(threshold);
  if (!values.length || !Number.isFinite(limit)) return null;
  return {
    matching: values.filter((value) => round2(value) <= limit).length,
    total: values.length,
    best: round2(Math.min(...values)),
  };
};

export const wineAlertThreshold = (rawWine) => thresholdFor(wineAlertCents(rawWine), WINE_ALERT_RANGE);

export const marketplaceAlertThreshold = (rawProduct) => thresholdFor(marketplaceAlertCents(rawProduct), MARKETPLACE_ALERT_RANGE);

// The account alert settings, with that threshold filled in (not saved).
export const alertSettingsHref = (source, threshold) => (
  threshold ? `/account?alert=${source}&threshold=${threshold}#alerts` : '/account#alerts'
);

// Reads ?alert=&threshold= from the account page; anything out of range is ignored.
export const parseAlertPrefill = (search) => {
  const params = new URLSearchParams(search || '');
  const source = params.get('alert');
  const threshold = Number(params.get('threshold'));
  const range = source === 'wine' ? WINE_ALERT_RANGE : source === 'marketplace' ? MARKETPLACE_ALERT_RANGE : null;
  if (!range || !Number.isFinite(threshold) || threshold < range.min || threshold > range.max) return null;
  if (Math.abs(threshold * 10 - Math.round(threshold * 10)) > 1e-9) return null;
  return { source, threshold: threshold.toFixed(1) };
};

// "30,678 points (30,000 bonus + 678 base) would cost about $818 bought from Qantas."
export const pointsBreakdown = ({ bonusPoints, basePoints }) => (
  basePoints > 0 ? `${Math.round(bonusPoints).toLocaleString('en-AU')} bonus + ${Math.round(basePoints).toLocaleString('en-AU')} base` : null
);

export const wholeDollars = (value) => formatAud(Math.round(value), { decimals: 0 });
